import React, { useRef, useMemo } from 'react';
import StoreRankDetailsCore from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import useStoreRankDetailsCoreUI from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore/hooks/useStoreRankDetailsCoreUI';
import { getDataSourceInfo } from '@/pages/intelligence/utils/getDataSourceInfo';
import { STORERANK_METRICS_OPTIONS_LIST } from '@/pages/intelligence/_config/storeRank/config';
import '@/pages/intelligence/assets/scss/TopCharts/MobileGames/components/StoreRank.scss';
import type { IBaseProps, IGameType } from '@/pages/intelligence/types/common';
import type { IStoreRankDetailsCoreInstance } from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore/types';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { getStoreRankDetailsCoreUIData } from '@/pages/intelligence/pages/TopCharts/components/StoreRankDetailsCore/utils/dataTransfer';

interface IStoreRankDetailsProps extends IBaseProps {
  entityType: IGameType;
  gameId: string;
  appIds?: any;
  appIdListAll?: any[];
  isBasicLoading: boolean;
  basicInfo: object | any;
  countryList: any[];
  firstWeekSinceReleaseDates?: string[];
}

const MarketCount: React.FC<IStoreRankDetailsProps> = (props) => {
  const {
    gameId,
    countryList = [],
    allLoading,
    fetchParams,
    tableDataMarket,
    composedPlatformCur,
    trendLoading,
    entityType,
    firstWeekSinceReleaseDates = [],
  } = props;

  // console.log('MarketCountMarketCount', props);

  const { formatMessage } = useIntlExtend();

  const loadingCur = useMemo(() => {
    return trendLoading || allLoading;
  }, [trendLoading, allLoading]);

  const platform = composedPlatformCur;
  const topValue =
    Array.isArray(fetchParams?.source) && fetchParams?.source?.length > 0
      ? fetchParams?.source?.[0]
      : '';
  const topValueMetricObj = STORERANK_METRICS_OPTIONS_LIST?.find(
    (item: any) => item.value === topValue,
  );
  const topLocaleKey = topValueMetricObj.textKey;
  const topLocaleText = topLocaleKey ? (formatMessage({ id: topLocaleKey }) as string) : topValue;
  const storeRankDetailsCore = useRef<IStoreRankDetailsCoreInstance>(null);

  const getRankName = (ranking: string) => {
    switch (ranking) {
      case '1-3':
        return 'rank_1_3';
      case '4-10':
        return 'rank_4_10';
      case '11-50':
        return 'rank_11_50';
      case '51-200':
        return 'rank_51_200';
    }
  };

  const getMetricDatas = (detailList: any[]) => {
    if (Array.isArray(detailList) && detailList?.length > 0) {
      return detailList?.map((item: any) => {
        return {
          name: getRankName(item.ranking),
          rank: item.quantity,
          rank_dod: item.dod,
          // markets: item.markets,
        };
      });
    }
    return [];
  };

  const sourceData = useMemo(() => {
    if (loadingCur) return [];
    if (Array.isArray(tableDataMarket) && tableDataMarket?.length > 0) {
      return tableDataMarket?.map((item: any) => {
        return {
          date: item.date,
          metric: getMetricDatas(item.country_detail),
          country_detail: item.country_detail,
        };
      });
    }
    return [];
  }, [tableDataMarket, loadingCur]);

  const platformInfo = getDataSourceInfo(fetchParams?.entity_type, platform);
  const { visualisOption, tablesOption } = useStoreRankDetailsCoreUI({
    isLoading: loadingCur,
    isSectionalRank: true,
    visualisHelpperOptionConfig: {
      tooltipHeaderLeftTitle: topLocaleText,
      tooltipHeaderLeftIcon: platformInfo?.icon,
      rankDimensionsNameFormatter: (dimension: string) => {
        const platformName = platformInfo?.name;
        const platformTopInfo = platformName && top ? [platformName, topLocaleText].join('_') : '';
        // 只选了单国家时，图例展示榜单名
        // if (fetchParams?.regions?.length === 1) {
        //   return platformTopInfo || '';
        // }
        return platformTopInfo && dimension ? `${platformTopInfo} ${dimension}` : '';
      },
    },
    tablesHelpperOptionConfig: {},
    data: sourceData,
  });
  const firstWeekVisualisOption = useMemo(() => {
    if (!firstWeekSinceReleaseDates.length) {
      return visualisOption;
    }
    const sourceDataMap = new Map(
      sourceData.map((item: any) => [formatDateStr(item?.date), item]),
    );
    const displayData = firstWeekSinceReleaseDates.map((date: string) => {
      const dateItem = sourceDataMap.get(date);
      if (dateItem) {
        return { ...dateItem, date };
      }
      return { date, metric: [] };
    });
    const { sourceDataDimensions, visualisSourceData } = getStoreRankDetailsCoreUIData(displayData);
    return {
      ...visualisOption,
      sourceDataDimensions,
      sourceData: visualisSourceData,
    };
  }, [visualisOption, sourceData, firstWeekSinceReleaseDates]);

  // 分段排名时，国家排名子表格 获取数据的方法
  const fetchCountriesRankData = async () => {};

  return (
    <>
      <StoreRankDetailsCore
        ref={storeRankDetailsCore}
        isLoading={loadingCur}
        gameId={gameId}
        title={formatMessage({ id: 'dbi_store_rank_visualis_card_title_multipe' })}
        countries={countryList}
        visualisOption={{ ...firstWeekVisualisOption, isAreaEChart: true, isYAxisInverse: false }}
        // visualisOption={visualisOption}
        tablesOption={{ ...tablesOption, isExpandable: true }}
        selectedMode={true}
        // selectedMode={fetchParams?.regions?.length === 1 ? 'single' : true}
        fetchCountriesRankData={fetchCountriesRankData}
        tableDataMarket={tableDataMarket}
        isNewOpenTable={true}
        entityType={entityType}
        currentFetchSource={fetchParams?.source || []}
      />
    </>
  );
};

export default MarketCount;
